/* eslint-disable no-unused-vars */
import React, { useState, useLayoutEffect, useEffect } from "react";
import styles from "./HowItWorks.module.css";
import { useHistory } from "react-router-dom";
import checkBrowserSupport from "../self-assesment/browserDetect";
import { AnalyticsService, BrowserBlockScreen } from "../self-assesment/SelfAssesment";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd";
import { SubPageHeader } from "./SubHeader";

// ============================================================
// CONSTANTS
// ============================================================
const LOGO_URL = "https://virdiodev.s3.us-east-2.amazonaws.com/sampleVideo/LogoPNG.png";

// ============================================================
// useScrollToTop
// ============================================================
const useScrollToTop = () => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
};

// ============================================================
// Step content data
// Each step has a title, body paragraphs, and an optional
// expandable section using the card-style accordion pattern.
// ============================================================
const STEPS = [
  {
    num: 1,
    title: "To understand how you are feeling, Myra asks a few questions",
    body: ["Using physical therapy best practices, Myra has put together questions about your pain that give her insights about what movements might help her understand your pain."],
    expandLabel: "See example questions",
    expandContent: (
      <>
        <p>First, you pick your pain area from a short list — lower back, hip, thigh, or knee. Then Myra narrows it down with a few targeted questions. For example, if you chose lower back:</p>
        <ul>
          <li>Which best describes your pain — a deep stiffness, a feeling of weakness, or somewhere in between?</li>
          <li>What tends to trigger it — sitting, standing, twisting, or staying in one position too long?</li>
          <li>What your current pain level is and how long you've been dealing with it</li>
        </ul>
      </>
    ),
  },
  {
    num: 2,
    title: "A quick check for your comfort and safety",
    body: ["Before Myra guides you through any movements, she asks a few simple questions about the specific area that is bothering you.", "We want to make sure this assessment is the right fit for you. These short, personalized questions help us figure out if you should talk to a doctor first. It is just a quick step designed to keep you safe and protect your well-being."],
    expandLabel: "See how the safety check works",
    expandContent: null, // handled inline with safety tabs
  },
  {
    num: 3,
    title: "Simple movement checks",
    body: ["Based on your responses, Myra chooses specific movements for you to do so she can confirm her understanding of your mobility. She can use your device's camera, or you can simply share how each movement felt."],
    expandLabel: "See example movement checks",
    expandContent: (
      <>
        <p>Myra picks 1 or 2 gentle movements based on your answers. She shows you a demo video first, then watches through your camera. Examples include:</p>
        <ul>
          <li>Standing Back Extension</li>
          <li>Seated Knee to Chest</li>
          <li>Side to Side Bend</li>
          <li>Seated Trunk Rotation</li>
          <li>Toe Touch</li>
        </ul>
        <p className={styles.expandNote}>The goal is never to push through pain. It's to help Myra understand your starting point.</p>
      </>
    ),
  },
  {
    num: 4,
    title: "The more Myra knows, the better she can plan your recovery",
    body: ["Myra does not guess; she knows that your recovery is based on your personal journey. The information she asks for is to ensure the exercises you get are the ones that will help you move with confidence.", "While the information is personal to your recovery, she does not ask for your email or have you sign up."],
    expandLabel: "See what information Myra uses and why",
    expandContent: (
      <>
        <p>After the movement checks, you can optionally share a few details like your age, activity level, balance, whether you can get to the floor, and your schedule preference.</p>
        <p>The more Myra knows, the more accurate your plan will be. If you skip this step, she'll still build a plan — but she'll have to make conservative assumptions about what's safe for you.</p>
      </>
    ),
  },
  {
    num: 5,
    title: "What you get at the end of the assessment",
    body: null, // step 4 uses a result list instead of body paragraphs
    resultList: ["Your first week's exercises to help you start feeling better", "A full explanation of why Myra picked each specific exercise", "The ability to ask questions about your personalized plan at any time"],
    expandLabel: "See an example recovery plan",
    expandContent: null, // handled inline with nested expand
  },
];

// ============================================================
// HowItWorksPage — main export
// ============================================================
export const HowItWorksPage = () => {
  useScrollToTop();
  const history = useHistory();
  const [showMemberBlockScreen, setShowMemberBlockScreen] = useState(false);
  const [isStartedLandingFlow, setIsStartedLandingFlow] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Track which step's expandable is open (null = all closed)
  const [openStep, setOpenStep] = useState(null);
  // Track the nested "See what Myra said" expand inside Step 5
  const [openNested, setOpenNested] = useState(false);
  // Track which safety tab is active
  const [safetyTab, setSafetyTab] = useState("lower-back");

  // ---- Session initialization on page load ----
  useEffect(() => {
    const previousSessionId = window.trSessionId || localStorage.getItem("trSessionId");
    if (previousSessionId) {
      localStorage.removeItem("trSessionId");
      window.trSessionId = null;
    }

    const isRepeat = localStorage.getItem("isUserCameBackAgain") === "true";
    localStorage.setItem("isUserCameBackAgain", "true");

    const params = new URLSearchParams(window.location.search);
    const gclid = params.get("gclid");
    const urlVariant = params.get("variant");
    const resolvedVariant = urlVariant || localStorage.getItem("variant") || null;

    if (urlVariant) {
      localStorage.setItem("variant", urlVariant);
    }

    const isFromGoogle = !!(gclid || resolvedVariant);

    const trackingFields = {
      variant: resolvedVariant,
      isComeFromGoogle: isFromGoogle ? 1 : 0,
      isUserCameBackAgain: isRepeat ? 1 : 0,
      visitedHowItWorks: 1,
    };

    AnalyticsService.startSession(trackingFields);
  }, []);

  const handleStartAssessment = () => {
    if (!window.trSessionId) {
      AnalyticsService.startSession({ visitedHowItWorks: 1 });
    }
    history.push("/problem-area");
  };
  if (showMemberBlockScreen) {
    return <BrowserBlockScreen featureType="member" />;
  }

  const toggleStep = (index) => {
    setOpenStep(openStep === index ? null : index);
    // Close nested if step 4 is being closed
    if (openStep === index) setOpenNested(false);
  };
  const handlePlayAudio = () => {
    const msg = "I understand that you're experiencing pain in your lower back and that this is a recent issue. Your back is also sensitive to holding positions, especially sitting. The most important thing we learned from your assessment is that the side to side bend was uncomfortable. That's perfectly okay — it just tells us we need to start gently. Your plan has 3 focused exercises. We'll warm up with the Seated Toe Tap to activate your core and back muscles. Your main exercise is the Seated Bird Dog, targeting your deep back muscles to help reduce pain. And we'll finish with the Seated Trunk Side Bending to help restore range of motion. If anything feels too hard or too easy, just let me know and I'll adjust the plan for you. I'm here with you the entire way.";
    window.currentFlow = "flow_landing_page";
    VoiceService._currentFlow = "flow_landing_page";
    window.initFromUserGesture();
    if (window.audioPlayer && window.audioPlayer.pcm) {
      // Stoping the any ongoing PCM playback
      if (typeof window.audioPlayer.ttsPcmAbort === "function") {
        window.audioPlayer.ttsPcmAbort();
      }
      // Force reset state
      window.audioPlayer.pcm.active = false;
      window.audioPlayer.pcm.queuedSamples = 0;
      window.audioPlayer.pcm.endReceived = false;
      window.audioPlayer.pcm.floatQueue = [];
      window.audioPlayer.pcm.floatQueueSamples = 0;
      window.audioPlayer.pcm.sources.clear();
    }
    if (VoiceService._ws && VoiceService._ws.readyState === WebSocket.OPEN) {
      VoiceService._ws.send(JSON.stringify({ type: "say", text: msg }));
    } else {
      VoiceService.initialize({
        onConnected: () => {
          VoiceService._ws.send(JSON.stringify({ type: "say", text: msg }));
          console.log("Connected voice service...");
        },
      });
      const flowToStart = VoiceService._currentFlow || window.currentFlow;
      // VoiceService.startFlowWithoutAnnouncement(flowToStart);
      // window.mayraCode.handleMicClick(true, true, false, true);
      // No connection at all — store pending and reconnect
      // VoiceService._pendingExitAnnouncement = msg;
      VoiceService.startFlowWithoutAnnouncement(flowToStart, "", msg);
    }
    // VoiceService._ws = null;
    if (window.MyraInteraction && window.MyraInteraction.getMode() !== "voice") {
      VoiceService._releaseMic();
    }
  };

  return (
    <div className={styles.page}>
      <SubPageHeader activePage="how-it-works" isStartedLandingFlow={true} setShowMemberBlockScreen={setShowMemberBlockScreen} />

      {/* ===== HERO ===== */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <h1 className={styles.heroHeadline}>How the Guided Self-Assessment works</h1>
          <p className={styles.heroSub}>Myra is your recovery coach, built on evidence-based research and informed by Doctors of Physical Therapy. While she is not a doctor or clinician, she is designed to help you guide your own recovery at home.</p>
          <p className={styles.heroSub2}>In about 10 minutes, Myra guides you through a few simple steps to better understand your pain and movement, then creates a recovery plan tailored to you.</p>
        </div>
      </section>

      {/* ===== STEPS ===== */}
      <section className={styles.stepsSection}>
        {/* ---- Step 1 ---- */}
        <div className={styles.step}>
          <div className={styles.stepHeader}>
            <div className={styles.stepNum}>1</div>
            <h3 className={styles.stepTitle}>{STEPS[0].title}</h3>
          </div>
          <div className={styles.stepBody}>
            {STEPS[0].body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}

            {/* Accordion expandable */}
            <div className={`${styles.accordionItem} ${openStep === 0 ? styles.accordionOpen : ""}`} style={{ marginTop: "14px" }}>
              <button className={styles.accordionTrigger} onClick={() => toggleStep(0)} aria-expanded={openStep === 0}>
                <span className={styles.accordionQuestion}>{STEPS[0].expandLabel}</span>
                <svg className={`${styles.accordionChevron} ${openStep === 0 ? styles.chevronOpen : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openStep === 0 && (
                <div className={styles.accordionBody}>
                  <div className={styles.expandContent}>{STEPS[0].expandContent}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <hr className={styles.stepDivider} />

        {/* ---- Step 2: Safety Check ---- */}
        <div className={styles.step}>
          <div className={styles.stepHeader}>
            <div className={styles.stepNum}>2</div>
            <h3 className={styles.stepTitle}>{STEPS[1].title}</h3>
          </div>
          <div className={styles.stepBody}>
            {STEPS[1].body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}

            <div className={`${styles.accordionItem} ${openStep === 1 ? styles.accordionOpen : ""}`} style={{ marginTop: "14px" }}>
              <button className={styles.accordionTrigger} onClick={() => toggleStep(1)} aria-expanded={openStep === 1}>
                <span className={styles.accordionQuestion}>{STEPS[1].expandLabel}</span>
                <svg className={`${styles.accordionChevron} ${openStep === 1 ? styles.chevronOpen : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openStep === 1 && (
                <div className={styles.accordionBody}>
                  <div className={styles.expandContent}>
                    <div className={styles.safetyTabs}>
                      {[
                        { key: "lower-back", label: "Lower back" },
                        { key: "hip", label: "Hip" },
                        { key: "thigh", label: "Thigh" },
                        { key: "knee", label: "Knee" },
                      ].map((tab) => (
                        <button key={tab.key} className={`${styles.safetyTab} ${safetyTab === tab.key ? styles.safetyTabActive : ""}`} onClick={() => setSafetyTab(tab.key)}>
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {safetyTab === "lower-back" && (
                      <>
                        <p>For example, if you have lower back pain, Myra may ask whether you have:</p>
                        <ul>
                          <li>Sharp or shooting pain down your legs</li>
                          <li>Numbness or tingling in your legs or feet</li>
                          <li>Leg weakness or instability</li>
                          <li>Changes in bowel or bladder control</li>
                        </ul>
                      </>
                    )}
                    {safetyTab === "hip" && (
                      <>
                        <p>For example, if you have hip pain, Myra may ask whether you have:</p>
                        <ul>
                          <li>Changes in bowel or bladder control</li>
                          <li>Shooting leg pain, numbness, or tingling</li>
                          <li>A childhood hip condition (such as Perthes disease or a slipped hip)</li>
                          <li>Avascular necrosis or bone death in the hip</li>
                          <li>A hip replacement or major hip surgery in the past 6 months</li>
                          <li>Hip pain that started after a fall or significant injury</li>
                        </ul>
                      </>
                    )}
                    {safetyTab === "thigh" && (
                      <>
                        <p>For example, if you have thigh pain, Myra may ask whether you have:</p>
                        <ul>
                          <li>Changes in bowel or bladder control</li>
                          <li>Shooting leg pain, numbness, or tingling</li>
                          <li>Thigh pain that started after a fall, direct hit, or other injury</li>
                          <li>Leg or thigh surgery in the past 3 months</li>
                        </ul>
                      </>
                    )}
                    {safetyTab === "knee" && (
                      <>
                        <p>For example, if you have knee pain, Myra may ask whether you have:</p>
                        <ul>
                          <li>Changes in bowel or bladder control</li>
                          <li>Numbness or tingling in your legs or feet</li>
                          <li>Leg weakness, instability, or your leg giving out</li>
                          <li>Knee surgery in the past 3 months</li>
                          <li>Knee pain that started after a fall, twist, or direct injury</li>
                          <li>A torn ligament (ACL, PCL, MCL) that has not been treated</li>
                        </ul>
                      </>
                    )}

                    <p className={styles.expandNote}>If any of these apply, Myra will simply recommend that you seek medical care before moving forward.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <hr className={styles.stepDivider} />

        {/* ---- Step 3: Movement Checks ---- */}
        <div className={styles.step}>
          <div className={styles.stepHeader}>
            <div className={styles.stepNum}>3</div>
            <h3 className={styles.stepTitle}>{STEPS[2].title}</h3>
          </div>
          <div className={styles.stepBody}>
            {STEPS[2].body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}

            <div className={`${styles.accordionItem} ${openStep === 2 ? styles.accordionOpen : ""}`} style={{ marginTop: "14px" }}>
              <button className={styles.accordionTrigger} onClick={() => toggleStep(2)} aria-expanded={openStep === 2}>
                <span className={styles.accordionQuestion}>{STEPS[2].expandLabel}</span>
                <svg className={`${styles.accordionChevron} ${openStep === 2 ? styles.chevronOpen : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openStep === 2 && (
                <div className={styles.accordionBody}>
                  <div className={styles.expandContent}>{STEPS[2].expandContent}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <hr className={styles.stepDivider} />

        {/* ---- Step 4: Information ---- */}
        <div className={styles.step}>
          <div className={styles.stepHeader}>
            <div className={styles.stepNum}>4</div>
            <h3 className={styles.stepTitle}>{STEPS[3].title}</h3>
          </div>
          <div className={styles.stepBody}>
            {STEPS[3].body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}

            <div className={`${styles.accordionItem} ${openStep === 3 ? styles.accordionOpen : ""}`} style={{ marginTop: "14px" }}>
              <button className={styles.accordionTrigger} onClick={() => toggleStep(3)} aria-expanded={openStep === 3}>
                <span className={styles.accordionQuestion}>{STEPS[3].expandLabel}</span>
                <svg className={`${styles.accordionChevron} ${openStep === 3 ? styles.chevronOpen : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openStep === 3 && (
                <div className={styles.accordionBody}>
                  <div className={styles.expandContent}>{STEPS[3].expandContent}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <hr className={styles.stepDivider} />

        {/* ---- Step 5: Results ---- */}
        <div className={styles.step}>
          <div className={styles.stepHeader}>
            <div className={styles.stepNum}>5</div>
            <h3 className={styles.stepTitle}>{STEPS[4].title}</h3>
          </div>
          <div className={styles.stepBody}>
            <ul className={styles.resultList}>
              {STEPS[4].resultList.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>

            {/* Example recovery plan accordion */}
            <div className={`${styles.accordionItem} ${openStep === 4 ? styles.accordionOpen : ""}`} style={{ marginTop: "14px" }}>
              <button className={styles.accordionTrigger} onClick={() => toggleStep(4)} aria-expanded={openStep === 4}>
                <span className={styles.accordionQuestion}>See an example recovery plan</span>
                <svg className={`${styles.accordionChevron} ${openStep === 4 ? styles.chevronOpen : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openStep === 4 && (
                <div className={styles.accordionBody}>
                  <div className={styles.expandContent}>
                    <p className={styles.planTitle}>Example: lower back pain plan</p>
                    <table className={styles.planTable}>
                      <thead>
                        <tr>
                          <th>Exercise</th>
                          <th>Sets</th>
                          <th>Reps</th>
                          <th>Hold</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className={styles.exerciseName}>Seated Toe Tap</td>
                          <td>2</td>
                          <td>12</td>
                          <td>—</td>
                        </tr>
                        <tr>
                          <td className={styles.exerciseName}>Seated Bird Dog</td>
                          <td>2</td>
                          <td>8</td>
                          <td>—</td>
                        </tr>
                        <tr>
                          <td className={styles.exerciseName}>Seated Trunk Side Bending</td>
                          <td>2</td>
                          <td>8</td>
                          <td>5 sec</td>
                        </tr>
                      </tbody>
                    </table>

                    {/* Nested expand — Myra's explanation */}
                    <button className={`${styles.nestedExpandTrigger} ${openNested ? styles.nestedOpen : ""}`} onClick={() => setOpenNested(!openNested)}>
                      {openNested ? "▾ Hide what Myra said about this plan" : "▸ See what Myra said about this plan"}
                    </button>
                    {openNested && (
                      <div className={styles.nestedExpandContent}>
                        "I understand that you're experiencing pain in your lower back and that this is a recent issue. Your back is also sensitive to holding positions, especially sitting. The most important thing we learned from your assessment is that the side to side bend was uncomfortable. That's perfectly okay — it just tells us we need to start gently. Your plan has 3 focused exercises. We'll warm up with the Seated Toe Tap to activate your core and back muscles. Your main exercise is the Seated Bird Dog, targeting your deep back muscles to help reduce pain. And we'll finish with the Seated Trunk Side Bending to help restore range of motion. If anything feels too hard or too easy, just let me know and I'll adjust the plan for you. I'm here with you the entire way."{" "}
                        <button className={styles.SPaudioButton} onClick={handlePlayAudio} type="button" aria-label="Play my analysis">
                          {/* Speaker Icon */}
                          <svg className={styles.iconPlay} viewBox="0 0 24 24" fill="currentColor">
                            {/* Speaker base */}
                            <path d="M3 9v6h4l5 4V5L7 9H3z" />
                            {/* Sound waves */}
                            <path d="M14.5 12c0-1.77 1.02-3.29 2.5-4.03v8.06A4.978 4.978 0 0 1 14.5 12z" />
                            <path d="M16.5 3.97v2.16c2.89 1 5 3.77 5 6.87s-2.11 5.87-5 6.87v2.16c4.01-1.1 7-4.74 7-9.03s-2.99-7.93-7-9.03z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaCard}>
            <h2 className={styles.ctaHeading}>Ready to get started?</h2>
            <p className={styles.ctaSubtext}>Start your free Guided Self-Assessment and see what Myra recommends for you.</p>
            <button className={styles.btnPrimary} onClick={handleStartAssessment}>
              Start My Free Assessment
            </button>
            <p className={styles.ctaReassurance}>Takes 10 minutes · No signup required · No app to download</p>
            <div className={styles.secondaryCtaRow}>
              <p className={styles.secondaryCtaLabel}>Have questions before starting?</p>
              <button className={styles.btnTextLink} onClick={() => history.push("/ask-myra")}>
                Ask Myra →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== DISCLAIMER ===== */}
      <div className={styles.disclaimer}>
        <div className={styles.container}>
          <p className={styles.disclaimerText}>StartFreePT provides educational guidance based on your input. It is not a substitute for medical advice, diagnosis, or treatment.</p>
        </div>
      </div>

      {/* ===== FOOTER ===== */}
      <footer className={styles.footer}>
        <div className={styles.container}>
          <p className={styles.footerText}>© 2025 Virdio Health. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
